// Browser analytics client (issue #343 / ADR-0020).
//
// A small buffered client that emits `page_view` and `interaction` events,
// batches them in-memory, and flushes to the server ingest endpoint:
//   * on an interval,
//   * on a buffer-size threshold,
//   * on `pagehide` / `visibilitychange`→hidden (so the final actions of a visit
//     are not lost).
//
// The client supplies CONTENT only — never identity. The server re-derives
// actor / persona / ip / user-agent from the session at ingest, so nothing here
// is trusted for identity (the trust boundary). Capture is best-effort: a failed
// send is swallowed and never surfaces to the user, and never blocks a page.

import type { AnalyticsClientEvent, AnalyticsClientEventKind } from '@septima/contract';
import { clockNow } from '@/lib/clock';

// Same base URL resolution as the typed api client (lib/api.ts): empty in dev
// (same-origin via the Vite /api proxy) and the absolute API origin in prod.
const BASE_URL = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';
const INGEST_URL = `${BASE_URL}/api/analytics/ingest`;

const FLUSH_INTERVAL_MS = 5000;
const FLUSH_THRESHOLD = 20;
/** Hard cap so a long-lived tab that can't reach the server never grows unbounded. */
const MAX_BUFFER = 500;
const SESSION_KEY = 'septima-analytics-session';

let buffer: AnalyticsClientEvent[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let listenersBound = false;
// The X-Request-Id of the most recent API response (set by lib/api.ts), so a
// subsequent frontend `error` row carries the SAME correlation id as the server
// api_request / trace_events it relates to (PRD #345 / ADR-0021, WS4).
let lastRequestId: string | null = null;

/** Stack/message caps — mirror the contract bounds (server also caps the stack). */
const STACK_CAP = 8192;
const MESSAGE_CAP = 2000;

export interface TrackPayload {
    path?: string;
    route?: string;
    objectType?: string;
    objectId?: string;
    details?: Record<string, unknown>;
}

/** A stable per-visit id (sessionStorage-backed). Survives navigations, not tabs. */
function sessionId(): string {
    try {
        let id = sessionStorage.getItem(SESSION_KEY);
        if (!id) {
            id = `sess-${crypto.randomUUID()}`;
            sessionStorage.setItem(SESSION_KEY, id);
        }
        return id;
    } catch {
        // Private mode / disabled storage — degrade to a non-persistent id.
        return 'sess-unavailable';
    }
}

/**
 * The stable per-visit id, exposed for request grouping (PRD #345, item 2b):
 * the typed api client stamps it as an `X-Session-Id` request header so the
 * server can group a browser visit's API calls without ever being sent
 * identity. Identical semantics to the private {@link sessionId} — no new state.
 */
export function analyticsSessionId(): string {
    return sessionId();
}

function currentPath(): string {
    return typeof window !== 'undefined' ? window.location.pathname : '';
}

/** Buffer one event. Drops oldest past the hard cap. Never throws. */
export function track(kind: AnalyticsClientEventKind, payload: TrackPayload = {}): void {
    const event: AnalyticsClientEvent = {
        kind,
        occurredAt: clockNow().toISOString(),
        sessionId: sessionId(),
        path: payload.path ?? currentPath(),
    };
    if (payload.route !== undefined) event.route = payload.route;
    if (payload.objectType !== undefined) event.objectType = payload.objectType;
    if (payload.objectId !== undefined) event.objectId = payload.objectId;
    if (payload.details !== undefined) event.details = payload.details;

    buffer.push(event);
    if (buffer.length > MAX_BUFFER) buffer.splice(0, buffer.length - MAX_BUFFER);
    if (buffer.length >= FLUSH_THRESHOLD) flush();
}

/** Convenience for the page-view signal (route change). */
export function trackPageView(pathname: string): void {
    track('page_view', { path: pathname });
}

/**
 * Record the server's `X-Request-Id` from the latest API response (called by
 * lib/api.ts). A subsequent `error` event reuses it as `correlationId`, joining
 * the frontend error to the server request/trace that preceded it.
 */
export function setLastRequestId(id: string | null | undefined): void {
    if (id) lastRequestId = id;
}

export interface ErrorPayload {
    name?: string;
    message: string;
    stack?: string;
    status?: number;
    route?: string;
    path?: string;
}

/**
 * Emit a frontend `error` event (CONTENT only — the server stamps identity +
 * correlation and routes it to trace_events). Flushes immediately: errors matter
 * and a crashing page may unload before the interval fires.
 */
export function trackError(payload: ErrorPayload): void {
    const event: AnalyticsClientEvent = {
        kind: 'error',
        occurredAt: clockNow().toISOString(),
        sessionId: sessionId(),
        path: payload.path ?? currentPath(),
        message: (payload.message || 'error').slice(0, MESSAGE_CAP),
    };
    if (payload.name) event.name = payload.name.slice(0, 200);
    if (payload.stack) event.stack = payload.stack.slice(0, STACK_CAP);
    if (payload.status !== undefined) event.status = payload.status;
    if (payload.route !== undefined) event.route = payload.route;
    if (lastRequestId) event.correlationId = lastRequestId;

    buffer.push(event);
    if (buffer.length > MAX_BUFFER) buffer.splice(0, buffer.length - MAX_BUFFER);
    flush();
}

function onWindowError(e: ErrorEvent): void {
    trackError({
        name: e.error instanceof Error ? e.error.name : 'Error',
        message: e.message || 'window.onerror',
        stack: e.error instanceof Error ? e.error.stack : undefined,
    });
}

function onUnhandledRejection(e: PromiseRejectionEvent): void {
    const reason = e.reason;
    if (reason instanceof Error) {
        trackError({ name: reason.name, message: reason.message, stack: reason.stack });
    } else {
        trackError({ name: 'UnhandledRejection', message: String(reason) });
    }
}

/** Send the buffered events. Best-effort; swallows all errors. */
export function flush(): void {
    if (buffer.length === 0) return;
    const events = buffer;
    buffer = [];
    const body = JSON.stringify({ events });

    // Prefer fetch + keepalive: it survives page unload AND works cross-origin
    // with credentials + CORS (unlike sendBeacon, which cannot preflight an
    // application/json body). sendBeacon is the fallback for browsers without
    // keepalive support.
    if (typeof fetch === 'function') {
        try {
            void fetch(INGEST_URL, {
                method: 'POST',
                credentials: 'include',
                keepalive: true,
                headers: { 'Content-Type': 'application/json' },
                body,
            }).catch(() => undefined);
            return;
        } catch {
            /* fall through to sendBeacon */
        }
    }
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        try {
            navigator.sendBeacon(INGEST_URL, new Blob([body], { type: 'application/json' }));
        } catch {
            /* best-effort — drop */
        }
    }
}

// Delegated click listener — broad, declarative coverage. Any element carrying a
// `data-track` attribute records an `interaction` when clicked; `data-track-*`
// dataset entries ride along in `details`.
function onDocumentClick(e: MouseEvent): void {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const el = target.closest<HTMLElement>('[data-track]');
    if (!el) return;
    const action = el.dataset.track;
    if (!action) return;
    const details: Record<string, unknown> = { action };
    for (const [key, value] of Object.entries(el.dataset)) {
        if (key !== 'track' && key.startsWith('track')) {
            // data-track-foo -> trackFoo -> foo
            const name = key.slice('track'.length);
            details[name.charAt(0).toLowerCase() + name.slice(1)] = value;
        }
    }
    track('interaction', { details });
}

function onVisibilityChange(): void {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') flush();
}

/** Start the interval timer + unload/visibility flush + data-track listener. Idempotent. */
export function startAnalytics(): void {
    if (listenersBound) return;
    listenersBound = true;
    timer = setInterval(flush, FLUSH_INTERVAL_MS);
    if (typeof window !== 'undefined') {
        window.addEventListener('pagehide', flush);
        window.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('error', onWindowError);
        window.addEventListener('unhandledrejection', onUnhandledRejection);
        document.addEventListener('click', onDocumentClick, { capture: true });
    }
}

/** Tear down (flushes the tail). Idempotent. */
export function stopAnalytics(): void {
    if (!listenersBound) return;
    listenersBound = false;
    if (timer) {
        clearInterval(timer);
        timer = null;
    }
    if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', flush);
        window.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('error', onWindowError);
        window.removeEventListener('unhandledrejection', onUnhandledRejection);
        document.removeEventListener('click', onDocumentClick, { capture: true });
    }
    flush();
}
